/**
 * Role vocabulary, shared by the client, the server and the harness so all
 * three cannot drift.
 *
 * The permission rules themselves are NOT here — they live in
 * convex/lib/authz.ts and are enforced on the server. This file is names only.
 */

export const ROLES = ["assistant", "manager", "warehouse", "owner"] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  assistant: "Dental assistant",
  manager: "Clinic manager",
  warehouse: "Warehouse admin",
  owner: "Owner",
};

/** The same labels mid-sentence: "…is now a clinic manager". Derived rather
 *  than typed twice, so a renamed role cannot end up sentence-case in one place
 *  and something else in the other. */
export const ROLE_LABELS_LOWER: Record<Role, string> = Object.fromEntries(
  ROLES.map((r) => [r, ROLE_LABELS[r].toLowerCase()]),
) as Record<Role, string>;

/**
 * Belongs to exactly one clinic, versus sees all of them.
 *
 * This partition and `isCrossClinic` in convex/lib/authz.ts are the same line
 * drawn twice, so one is defined as the negation of the other and both live
 * here. A role that is neither is a role the scoping helpers cannot place.
 */
export const isCrossClinic = (role: Role): boolean =>
  role === "owner" || role === "warehouse";

export const isClinicScoped = (role: Role): boolean => !isCrossClinic(role);

/** Dee manages all five clinics — she is an owner-level user, not a clinic
 *  manager. Getting that wrong would put the all-clinic view behind the wrong
 *  account at the demo. */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  assistant: "Submits requests and confirms deliveries. Sees no prices.",
  manager: "Approves their own clinic's requests and sees its invoices.",
  warehouse: "Manages stock and dispatches. Sees unit cost, not margin.",
  owner: "All clinics, all reports, pricing rules and margin.",
};
